import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TabNav from '../../composants/TabNav/TabNav';
import Button from '../../composants/Button/Button';
import CompteARebourd from '../../composants/CompteARebourd/CompteARebourd';
import TableauDeBord from '../../composants/TableauDeBord/TableauDeBord';
import SearchBar from '../../composants/SearchBar/SearchBar';
import CreerCandidatureModal from './components/CreerCandidatureModal/CreerCandidatureModal';
import MesCandidaturesTab, {
  CandidatureResumee,
  InvitationGroupementResumee,
} from './components/MesCandidaturesTab/MesCandidaturesTab';
import './Publications.css';

let FilterIcon: any = null;
try {
  FilterIcon = require('../../assets/icons/filter.svg').ReactComponent;
} catch (_) { }

// ── Types — alignés sur la réponse réelle de /publications/accord-cadre ──────

interface AccordCadreData {
  id: number;
  nom: string;
  annee: number;
  statut: 'brouillon' | 'configure' | 'ouvert' | 'ferme';
  nb_articles: number;
}

interface Publication {
  id: number;
  ref_avis: string;
  publie_le: string;
  acheteur: string;
  objet: string;
  categorie_cpv?: string;
  lieu_execution?: string;
  limite_depot: string;
  type: string;
  dossier_consultation?: string;
  questions_reponses: number;
  action: 'candidater' | 'reprendre' | 'reserve';
}

type OngletFilter = 'Tous' | 'AAPC' | 'Avis modificatifs' | "Avis d'attributions" | 'Mes candidatures';

const ONGLETS: OngletFilter[] = ['Tous', 'AAPC', 'Avis modificatifs', "Avis d'attributions", 'Mes candidatures'];

// Onglets 2 et 3 désactivés avec tooltip "Fonctionnalité en cours de développement"
const INACTIVE_TABS = [2, 3];

// ── Page principale ───────────────────────────────────────────────────────────

const Publications: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);

  // Ouvre l'onglet demandé via ?tab=mes-candidatures (ex : redirect depuis /publications/ac27)
  const initialTab = searchParams.get('tab') === 'mes-candidatures'
    ? ONGLETS.indexOf('Mes candidatures')
    : 0;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [search, setSearch] = useState('');
  const handleSearch = (q: string) => setSearch(q);
  const [showCreerModal, setShowCreerModal] = useState(false);
  const [accordCadreId, setAccordCadreId] = useState<number | null>(null);

  // Story 9.0 — Onglet "Mes candidatures"
  const [candidatures, setCandidatures] = useState<CandidatureResumee[]>([]);
  const [invitations, setInvitations] = useState<InvitationGroupementResumee[]>([]);
  const [loadingCandidatures, setLoadingCandidatures] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    // Charger la liste des publications
    fetch(`${process.env.REACT_APP_API_URL}/publications`, { headers })
      .then((r) => r.json())
      .then((data) => {
        const raw = data?.data;
        setPublications(Array.isArray(raw) ? raw : []);
      })
      .catch(() => setPublications([]))
      .finally(() => setLoading(false));

    // Charger l'accord-cadre courant pour récupérer son id
    fetch(`${process.env.REACT_APP_API_URL}/publications/ac27`, { headers })
      .then((r) => r.json())
      .then((data) => {
        const id = data?.data?.id ?? null;
        if (id) setAccordCadreId(id);
      })
      .catch(() => { /* accord-cadre non disponible */ });

    // Story 9.0 — Fetch candidatures agrégées + invitations groupement (parallèle)
    Promise.all([
      fetch(`${process.env.REACT_APP_API_URL}/candidatures`, { headers })
        .then((r) => r.json())
        .then((data) => (Array.isArray(data?.data) ? data.data : [])),
      fetch(`${process.env.REACT_APP_API_URL}/publications/ac27/groupement-invitations`, { headers })
        .then((r) => r.json())
        .then((data) => (Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []))),
    ])
      .then(([cands, invs]) => {
        setCandidatures(cands);
        setInvitations(invs);
      })
      .catch(() => {
        setCandidatures([]);
        setInvitations([]);
      })
      .finally(() => setLoadingCandidatures(false));
  }, []);

  const filtered = useMemo(() => {
    let list = publications;

    // Filtre onglet
    const onglet = ONGLETS[activeTab];
    if (onglet === 'AAPC') list = list.filter((p) => p.type === 'AAPC');
    else if (onglet === 'Avis modificatifs') list = list.filter((p) => p.type === 'Avis modificatif');
    else if (onglet === "Avis d'attributions") list = list.filter((p) => p.type === "Avis d'attribution");

    // Filtre recherche (ref, objet, acheteur)
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.ref_avis.toLowerCase().includes(q) ||
          p.objet.toLowerCase().includes(q) ||
          p.acheteur.toLowerCase().includes(q),
      );
    }

    return list;
  }, [publications, activeTab, search]);

  const columns = useMemo(() => [
    {
      field: 'ref_avis',
      header: 'Ref avis',
      renderCell: (value: string) => (
        <div className="text-truncate" title={value}>{value}</div>
      ),
    },
    {
      field: 'publie_le',
      header: 'Publié le',
      renderCell: (value: string) => {
        const d = value ? new Date(value) : null;
        return <div>{d && !isNaN(d.getTime()) ? d.toLocaleDateString('fr-FR') : '—'}</div>;
      },
    },
    {
      field: 'acheteur',
      header: 'Acheteur',
      renderCell: (value: string) => (
        <div className="text-truncate" title={value}>{value}</div>
      ),
    },
    {
      field: 'objet',
      header: 'Objet',
      renderCell: (value: string) => (
        <div className="text-truncate" title={value}>{value}</div>
      ),
    },
    {
      field: 'categorie_cpv',
      header: 'Cat. CPV',
      renderCell: (value: string) => <div>{value || '—'}</div>,
    },
    {
      field: 'lieu_execution',
      header: "Lieu d'exécution",
      renderCell: (value: string) => (
        <div className="text-truncate" title={value}>{value}</div>
      ),
    },
    {
      field: 'limite_depot',
      header: 'Limite de dépôt',
      renderCell: (value: string) => {
        const d = value ? new Date(value) : null;
        return <div>{d && !isNaN(d.getTime()) ? d.toLocaleString('fr-FR') : '—'}</div>;
      },
    },
    {
      field: 'limite_depot',
      header: 'Temps restant',
      renderCell: (value: string) => (
        <CompteARebourd targetDate={value} autoReload={false} />
      ),
    },
    {
      field: 'type',
      header: 'Type',
      renderCell: (value: string) => <div>{value}</div>,
    },
    {
      field: 'dossier_consultation',
      header: 'Dossier de consultation',
      renderCell: (value: string) => (
        <div className="text-truncate" title={value}>{value}</div>
      ),
    },
    {
      field: 'questions_reponses',
      header: 'Questions/réponses',
      renderCell: (value: number) => (
        <div>{value} échange{value > 1 ? 's' : ''}</div>
      ),
    },
    {
      field: 'action',
      header: 'Action',
      renderCell: (value: Publication['action'], row: Publication) => {
        if (value === 'reserve') {
          return (
            <Button
              type="button"
              variant="secondary"
              value="Réservé aux titulaires"
              size="small"
              disabled
            />
          );
        }
        if (value === 'reprendre') {
          return (
            <Button
              type="button"
              variant="primary"
              value="Reprendre"
              size="small"
              onClick={() => setActiveTab(ONGLETS.indexOf('Mes candidatures'))}
            />
          );
        }
        return (
          <Button
            type="button"
            variant="primary"
            value="Nouvelle candidature"
            size="small"
            onClick={() => setShowCreerModal(true)}
            disabled={!accordCadreId}
          />
        );
      },
    },
  ], [navigate, accordCadreId, setActiveTab]);

  const isMesCandidaturesTab = activeTab === ONGLETS.indexOf('Mes candidatures');

  const refreshCandidatures = () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    setLoadingCandidatures(true);
    Promise.all([
      fetch(`${process.env.REACT_APP_API_URL}/candidatures`, { headers })
        .then((r) => r.json())
        .then((data) => (Array.isArray(data?.data) ? data.data : [])),
      fetch(`${process.env.REACT_APP_API_URL}/publications/ac27/groupement-invitations`, { headers })
        .then((r) => r.json())
        .then((data) => (Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []))),
    ])
      .then(([cands, invs]) => {
        setCandidatures(cands);
        setInvitations(invs);
      })
      .catch(() => {
        setCandidatures([]);
        setInvitations([]);
      })
      .finally(() => setLoadingCandidatures(false));
  };

  return (
    <div className="publications-container">
      {/* Header */}
      <div className="publications-header">
        <h1>Publications</h1>
      </div>

      {/* Onglets — 2 et 3 désactivés */}
      <TabNav
        tabs={ONGLETS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        inactiveTabs={INACTIVE_TABS}
        inactiveTabsTooltips={{}}
      />

      {/* Onglet "Mes candidatures" */}
      {isMesCandidaturesTab ? (
        <MesCandidaturesTab
          candidatures={candidatures}
          invitations={invitations}
          loading={loadingCandidatures}
          onRefresh={refreshCandidatures}
        />
      ) : (
        <>
          {/* Barre de recherche + filtre */}
          <div className="button-group">
            <div className="button-filter-group">
              <SearchBar
                onSearch={handleSearch}
                onClear={() => handleSearch('')}
                placeholder="Trouver une publication"
              />
              <Button
                type="button"
                variant="third"
                value="filtrer"
                size="small"
                icon={FilterIcon ? <FilterIcon /> : null}
              />
            </div>
          </div>

          {/* Table via TableauDeBord */}
          {loading ? (
            <div className="loading-indicator">
              <div className="spinner"></div>
              <p>Chargement des publications en cours...</p>
            </div>
          ) : (
            <TableauDeBord
              data={filtered}
              columns={columns}
              showActionsColumn={false}
              isSelectable={false}
              showActionBar={false}
              selectedItems={[]}
              onSelectionChange={() => { }}
            />
          )}
        </>
      )}

      {/* Modal création candidature */}
      {showCreerModal && accordCadreId && (
        <CreerCandidatureModal
          accordCadreId={accordCadreId}
          onClose={() => setShowCreerModal(false)}
          onSuccess={(id) => {
            setShowCreerModal(false);
            navigate(`/publications/ac27/candidatures/${id}`);
          }}
        />
      )}
    </div>
  );
};

export default Publications;
